import React, { useState } from "react";
import { Check, Loader2Icon } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useMeQuery } from "@/queries/queries";
import { useCompanyESPMdata } from "@/queries/useCompanyQueries";
import api from "@/api/base";

import { Button } from "@/components/shared/ui/button";
import { Header, Text } from "@/components/shared/ui/text";
import CopyButtonWithToast from "@/components/icons/CopyButtonWithToast";

const ConnectAccount: React.FC = () => {
  const { data: user, isLoading: isUserLoading } = useMeQuery();
  const { isLoading: isESPMLoading } = useCompanyESPMdata(
    user?.company?.id || "",
    true,
  );

  const [activateContactStatus, setActivateContactStatus] = useState<
    string | null
  >(null);
  const queryClient = useQueryClient();
  const companyId = user?.company.id;

  const handleActivateContactClick = async () => {
    setActivateContactStatus("loading");
    await api.post(`/company/${companyId}/refresh_espm_data/`).then(() => {
      queryClient.invalidateQueries();
    });
    setActivateContactStatus("success");
  };

  if (isUserLoading || isESPMLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader2Icon className="animate-spin" size={24} />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex flex-col gap-6">
        <section className="flex flex-col gap-2">
          <Header className="text-md font-semibold">
            First: Add Carbon Clear as a contact in Portfolio Manager
          </Header>
          <Header className="text-md">
            Click{" "}
            <a
              href="https://portfoliomanager.energystar.gov/pm/contact/search"
              target="_blank"
              className="underline text-blue-500 inline"
              rel="noreferrer"
            >
              here
            </a>{" "}
            to search for contacts, or navigate to the search page by clicking
            "Contacts" in the header, then "Add New Contacts/Connections"
          </Header>
          <Header className="text-md">
            Search for "carbon_clear" in the Username field, then click the
            "Connect" button.
          </Header>
          <div className="flex flex-row gap-2 py-2 pr-4">
            <img
              src="/ESConnect_Step1.png"
              alt="Step1"
              className="max-w-full h-auto"
            />
          </div>
        </section>

        <div className="border-t" />

        <section className="flex flex-col gap-4">
          <Header className="text-md font-semibold">
            Then: Send us a connection request
          </Header>
          <ol className="list-decimal text-gray-600">
            <li>
              <Text className="text-md">
                <span>Enter </span>
                <span className="font-bold text-gray-800 inline-flex items-center">
                  <span>{companyId}</span>
                  <CopyButtonWithToast
                    copyContent={companyId}
                    size={12}
                    className="pl-2 pr-3 inline"
                  />
                </span>
                <span>
                  {" "}
                  as your Carbon Clear Company ID and click "Send Connection
                  Request".
                </span>
              </Text>
            </li>
            <li>
              <Text className="text-md">
                Then click the button below to have us accept your request.
              </Text>
            </li>
          </ol>
          <div className="flex flex-row gap-2 py-2 pr-4">
            <img
              src="/ESPM_Connect_Step3.png"
              alt="Step2"
              className="max-w-full h-auto"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={handleActivateContactClick}
              disabled={activateContactStatus === "loading"}
            >
              I've sent the request — check now
            </Button>
            {activateContactStatus === "loading" ? (
              <Loader2Icon className="animate-spin" size={18} />
            ) : null}
            {activateContactStatus === "success" ? (
              <Check className="stroke-green-500" size={18} />
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ConnectAccount;
